import { useQuery } from "@tanstack/react-query";
import {
  type Fleet,
  getFleetWithCurrencyByCity,
} from "@/apis/reservations/apis";
import type { CityWhitelabelMapping } from "@/types/reducer-states";
import { isSuperAdmin } from "@/utils/utils";
import {
  useAdminDetails,
  useCitiesList,
  useCityWhitelabelMapping,
  useSavedWhitelabel,
} from "@/v2/stores";
import { queryKeys } from "./keys";

type FleetsByPaymentConfig = Record<string, Fleet>;

type FleetCurrencies = Record<string, string>;

interface FleetsData {
  fleetsByPaymentConfig: FleetsByPaymentConfig;
  fleetNamesByPaymentConfig: Record<string, string[]>;
  fleetCurrencies: FleetCurrencies;
}

interface UseFleetsOptions {
  enabled?: boolean;
  /**
   * Limit the sweep to one city.
   *
   * The backend enforces exactly one fleet per city (`resolve_city_id_to_fleet_id`
   * raises on more than one), so a view already scoped to a single city can only
   * ever need that city's fleet -- one request instead of one per accessible
   * city, which is 116 of them on dev.
   *
   * Omit it where every fleet is genuinely needed, such as a fleet picker.
   */
  cityId?: number | null;
}

/**
 * Fetches all fleets grouped by payment configuration ID
 * for cities accessible to the current admin.
 *
 * Uses TanStack Query for automatic caching and deduplication.
 * Multiple components can call this hook without triggering duplicate API requests.
 */
export function useFleetsByPaymentConfig(options?: UseFleetsOptions) {
  const adminDetails = useAdminDetails();
  const cityWhitelabelMapping = useCityWhitelabelMapping();
  const citiesList = useCitiesList();
  const savedWhitelabel = useSavedWhitelabel();

  const scopedCityId = options?.cityId ?? null;

  const { data, isLoading } = useQuery<FleetsData>({
    queryKey: [
      ...queryKeys.fleets.byPaymentConfig(),
      isSuperAdmin(adminDetails) ? (savedWhitelabel ?? "all") : "scoped",
      scopedCityId ?? "all-cities",
    ],
    queryFn: async () => {
      const cityIds = new Set<number>();

      if (scopedCityId !== null) {
        cityIds.add(scopedCityId);
      } else if (isSuperAdmin(adminDetails)) {
        if (!cityWhitelabelMapping) {
          return {
            fleetsByPaymentConfig: {},
            fleetNamesByPaymentConfig: {},
            fleetCurrencies: {},
          };
        }
        const whitelabels = savedWhitelabel
          ? cityWhitelabelMapping.filter(
              (w: CityWhitelabelMapping) => w.id === savedWhitelabel
            )
          : cityWhitelabelMapping;
        whitelabels.forEach((whitelabel: CityWhitelabelMapping) => {
          whitelabel.cities?.forEach((city) => {
            cityIds.add(city.id);
          });
        });
      } else if (adminDetails?.cityId) {
        cityIds.add(adminDetails.cityId);
      } else if (citiesList && citiesList.length > 0) {
        citiesList.forEach((city) => {
          cityIds.add(city.id);
        });
      }

      const fleetMap: FleetsByPaymentConfig = {};
      const fleetNamesMap: Record<string, string[]> = {};
      const currencyMap: FleetCurrencies = {};

      await Promise.all(
        Array.from(cityIds).map(async (cityId) => {
          try {
            const { fleet, currency } =
              await getFleetWithCurrencyByCity(cityId);
            if (
              fleet?.payment_configuration_id &&
              !fleetMap[fleet.payment_configuration_id]
            ) {
              fleetMap[fleet.payment_configuration_id] = fleet;
              if (currency) {
                currencyMap[fleet.payment_configuration_id] = currency;
              }
            }
            if (fleet?.payment_configuration_id) {
              const fleetNames =
                fleetNamesMap[fleet.payment_configuration_id] ?? [];
              if (!fleetNames.includes(fleet.name)) {
                fleetNames.push(fleet.name);
                fleetNamesMap[fleet.payment_configuration_id] = fleetNames;
              }
            }
          } catch {
            // Fleet fetch failed for this city - continue with other cities
          }
        })
      );

      return {
        fleetsByPaymentConfig: fleetMap,
        fleetNamesByPaymentConfig: fleetNamesMap,
        fleetCurrencies: currencyMap,
      };
    },
    enabled:
      (options?.enabled ?? true) &&
      (scopedCityId !== null ||
        (isSuperAdmin(adminDetails)
          ? Boolean(cityWhitelabelMapping)
          : Boolean(
              adminDetails?.cityId || (citiesList && citiesList.length > 0)
            ))),
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  const fleetsByPaymentConfig = data?.fleetsByPaymentConfig || {};
  const fleetNamesByPaymentConfig = data?.fleetNamesByPaymentConfig || {};
  const fleetCurrencies = data?.fleetCurrencies || {};

  const getFleetName = (paymentConfigId: string) => {
    const fleet = fleetsByPaymentConfig[paymentConfigId];
    if (fleet) return fleet.name;
    return paymentConfigId ? `Config ${paymentConfigId.slice(0, 8)}...` : "-";
  };

  const getCurrency = (paymentConfigId: string) => {
    return fleetCurrencies[paymentConfigId] || null;
  };

  return {
    fleetsByPaymentConfig,
    fleetNamesByPaymentConfig,
    fleetCurrencies,
    loading: isLoading,
    getFleetName,
    getCurrency,
  };
}
